import type React from 'react';

import { Link } from '@change/ds-core-ui/Link';
import { Text } from '@change/ds-core-ui/Text';
import { ExpandIcon } from '@change/ds-icons/ExpandIcon';
import { cn } from '@change/ds-utils';

import { useCalculatingResponsiveness } from './hooks/useCalculatingResponsiveness';

export const CalculatingResponsiveness: React.FC<EmptyProps> = () => {
	const {
		data: {
			isTagExpanded,
			responsivenessRef,
			expandButtonAriaLabel,
			contactDetails,
			tags,
			scoreTag,
			respondedToPetitions,
			totalRelevantPetitions,
			hasCountValues,
		},
		actions: { toggleTagExpansion, trackEmailLinkClick },
	} = useCalculatingResponsiveness();

	return (
		<div className="flex flex-col items-center" ref={responsivenessRef}>
			<button
				type="button"
				onClick={toggleTagExpansion}
				data-qa="calculating-responsiveness"
				className="focus-visible:outline-stroke-focus flex items-center text-sm focus:rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
				aria-label={expandButtonAriaLabel}
			>
				<span>How do we calculate this? </span>
				<ExpandIcon
					className={cn(
						'transition-transform',

						{ 'rotate-180': isTagExpanded },
					)}
				/>
			</button>

			{isTagExpanded && (
				<div className="mt-8" data-testid="how-to-calculate-responsiveness-content">
					<Text as="p" className="pb-4">
						Change.org calculates a responsiveness score based on how many times a public official responds to petitions
						with over 100 signatures from their district. Only petitions started in 2025 are included in the score.
					</Text>

					<div className="mb-4 flex flex-col items-center">
						{tags.map((tag) => (
							<div
								key={tag.label}
								className="my-1 inline-block rounded-full px-3 py-1"
								style={{ backgroundColor: tag.labelColor, color: tag.textColor }}
							>
								<span className="mr-2">{tag.rangeText}</span>
								<span className="mr-2 font-bold">{tag.label}</span>
							</div>
						))}
					</div>
					{hasCountValues && (
						<Text as="p" className="pb-4">
							Since this elected official has responded to <span className="font-bold">{respondedToPetitions}</span> out
							of <span className="font-bold">{totalRelevantPetitions}</span> petitions, they have been rated{' '}
							<span className="font-bold">{scoreTag?.label}</span>.
						</Text>
					)}

					<div>
						<p className="pb-4">
							Are you this elected official? To learn more about your Responsiveness Score and how to improve it, get in
							touch with us at:
						</p>
						<ul>
							<li> &bull; {contactDetails.phone} </li>
							<li>
								&bull; or email{' '}
								<Link
									href={contactDetails.emailHref}
									data-qa="dm_profile_change.org_email"
									target="_blank"
									onClick={() => trackEmailLinkClick()}
									className="no-underline"
								>
									<span className="text-typography-strong font-bold">{contactDetails.email}</span>
								</Link>
							</li>
						</ul>
					</div>
				</div>
			)}
		</div>
	);
};
